import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';

import { type AIAnswerTab, type QueryIntent } from '../constants';
import { sha256Hash } from '../utils';

import { filterCommonAttributes } from './analytics-helpers';
import type { commonAttributesType, NonGenerativeAIAnswerResultShownProps } from './types';

export const onAIAnswerDialogQueryIntent: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'surfaced',
		actionSubject: 'aiAnswerDialog',
		actionSubjectId: 'queryIntent',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the ai answer dialog is closed while loading before answer is shown
export const onAIAnswerDialogDroppedOff: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'dropoff',
		actionSubject: 'aiAnswerDialog',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the ai answer dialog exits the viewport
export const onAIAnswerDialogHidden: (props: commonAttributesType, dwellTime: number) => AnalyticsEventPayload = (
	props: commonAttributesType,
	dwellTime: number,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'hidden',
		actionSubject: 'aiAnswerDialog',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			dwellTime,
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the ai answer dialog is actually in the viewport
export const onAIAnswerDialogViewed: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'viewed',
		actionSubject: 'aiAnswerDialog',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the ai answer loading dialog is initially shown to the user
export const onAIAnswerLoadingDialogShown: (props: commonAttributesType, isAnimated?: boolean) => AnalyticsEventPayload = (
	props: commonAttributesType,
	isAnimated?: boolean,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerLoadingDialog',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			isAnimated,
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When query intent detection api classifies intent, routes the ui position of a smart answer
export const onSmartAnswerRouteDetection: (props: commonAttributesType, smartAnswersRoute: QueryIntent) => AnalyticsEventPayload = (
	props: commonAttributesType,
	smartAnswersRoute: QueryIntent,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'classified',
		actionSubject: 'aiAnswerRoute',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			smartAnswersRoute: smartAnswersRoute,
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

export const onAIAnswerDialogUnmount: ({ isReadingAids, sourceProduct, dwellTime, loadTime, fmpLoadTime, ...props }: commonAttributesType & {
	isReadingAids: boolean;
	searchSessionId: string;
	source: string;
	sourceProduct?: string;
	dwellTime?: number;
	loadTime?: number;
	fmpLoadTime?: number;
}) => AnalyticsEventPayload = ({
	isReadingAids,
	sourceProduct,
	dwellTime,
	loadTime,
	fmpLoadTime,
	...props
}: commonAttributesType & {
	isReadingAids: boolean;
	searchSessionId: string;
	source: string;
	sourceProduct?: string;
	dwellTime?: number;
	loadTime?: number;
	fmpLoadTime?: number;
}): AnalyticsEventPayload => {
		const { query, source, searchSessionId } = props;
		return {
			action: 'unmounted',
			actionSubject: 'aiAnswerDialog',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				aiFeatureName: isReadingAids
					? `Definitions ${sourceProduct ? `(${sourceProduct})` : ''}`
					: 'smartAnswers',
				aiInteractionId: isReadingAids ? undefined : searchSessionId,
				aiInteractionType: isReadingAids ? undefined : 'searchSessionId',
				dwellTime,
				loadTime,
				fmpLoadTime,
			},
			nonPrivacySafeAttributes: {
				query,
			},
		};
	};

// When the ai answer result is successfully displayed to the user
export const onAIAnswerResultShown: ({ isStreamed, loadTime, fmpLoadTime, ...props }: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
}) => AnalyticsEventPayload = ({
	isStreamed,
	loadTime,
	fmpLoadTime,
	...props
}: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
}): AnalyticsEventPayload => {
		const { query, answerString, source, cloudId } = props;
		return {
			action: 'shown',
			actionSubject: 'aiAnswerResult',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				cloudId,
				isStreamed,
				loadTime,
				fmpLoadTime,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

// Fires the same event as onAIAnswerResultShown but with less required attributes
// since some experiences don't have a plain text answer (e.g. Jira Issue Knowledge Card)
export const onNonGenerativeAIAnswerResultShown: ({ searchSessionId, queryHash, brand, source, query, answerString, answerCardType, fmpLoadTime, resourceType, }: NonGenerativeAIAnswerResultShownProps) => AnalyticsEventPayload = ({
	searchSessionId,
	queryHash,
	brand,
	source,
	query,
	answerString,
	answerCardType,
	fmpLoadTime,
	resourceType,
}: NonGenerativeAIAnswerResultShownProps): AnalyticsEventPayload => {
	return {
		action: 'shown',
		actionSubject: 'aiAnswerResult',
		source: source,
		eventType: 'track',
		attributes: {
			searchSessionId,
			queryHash,
			brand,
			source,
			answerCardType,
			fmpLoadTime,
			resourceType,
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// When there is no ai generated answer to the question
export const onAIAnswerNotShown: ({ isStreamed, loadTime, fmpLoadTime, ...props }: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
}) => AnalyticsEventPayload = ({
	isStreamed,
	loadTime,
	fmpLoadTime,
	...props
}: commonAttributesType & {
	isStreamed: boolean;
	loadTime?: number;
	fmpLoadTime?: number;
}): AnalyticsEventPayload => {
		const { query, answerString, source, errorState } = props;
		return {
			action: 'shown',
			actionSubject: 'aiAnswerError',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				isStreamed,
				loadTime,
				fmpLoadTime,
				errorState,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

// Tracking clicks on the expand button in SAIN answer cards
export const aiAnswerExpandClicked: ({ ...props }: commonAttributesType) => AnalyticsEventPayload = ({
	...props
}: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerExpand',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Tracking clicks on the collapse button in SAIN answer cards
export const aiAnswerCollapseClicked: ({ ...props }: commonAttributesType) => AnalyticsEventPayload = ({
	...props
}: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerCollapse',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Tracking when the SAIN answer card is displayed in the expanded state, ie the answer is too short to collapse.
export const aiAnswerExpandShown: ({ ...props }: commonAttributesType) => AnalyticsEventPayload = ({ ...props }: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerExpand',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// When the user clicks the source links from the ai generated answer
export const onAIAnswerSourceClick: ({ sourceIndex, isCitation, hostname, sourceCardType, sourceProduct, ...props }: commonAttributesType & {
	sourceIndex: number;
	isCitation?: boolean;
	hostname?: string;
	sourceCardType?: string;
}) => AnalyticsEventPayload = ({
	sourceIndex,
	isCitation,
	hostname,
	sourceCardType,
	sourceProduct,
	...props
}: commonAttributesType & {
	sourceIndex: number;
	isCitation?: boolean;
	hostname?: string;
	sourceCardType?: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerSourceLink',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				sourceIndex,
				isCitation,
				sourceCardType,
				sourceProduct,
				...(hostname
					? {
						metadata: {
							hostname,
						},
					}
					: {}),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onAIAnswerSourceContextMenuClick: ({ sourceIndex, ...props }: commonAttributesType & {
	sourceIndex: number;
}) => AnalyticsEventPayload = ({
	sourceIndex,
	...props
}: commonAttributesType & { sourceIndex: number }): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'contextMenuClicked',
			actionSubject: 'aiAnswerSourceLink',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				sourceIndex,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

// When the user clicks the expand toggle button for the source section of the ai answers result
export const onAIAnswerSourceExpandToggleClick: ({ expanded, ...props }: commonAttributesType & {
	expanded: boolean;
}) => AnalyticsEventPayload = ({
	expanded,
	...props
}: commonAttributesType & { expanded: boolean }): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerSourceLink',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				expanded,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

// When the user clicks the return link to get back to the search results from the ai generated answer
export const onAIAnswerReturnLinkClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerReturnLink',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// When the user clicks the like button for the ai generated answer
export const onLikeButtonClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerLikeButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onDislikeButtonClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerDislikeButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onDeselectLikeButtonClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerDeselectLikeButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onDeselectDislikeButtonClick: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerDeselectDislikeButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onFeedbackSubmit: (props: commonAttributesType & {
	feedbackSentiment: "positive" | "negative";
	feedbackOptions: string[];
	additionalFeedbackAdded: boolean;
	includeResponse: boolean;
	canContact: boolean;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		feedbackSentiment: 'positive' | 'negative';
		feedbackOptions: string[];
		additionalFeedbackAdded: boolean;
		includeResponse: boolean;
		canContact: boolean;
	},
): AnalyticsEventPayload => {
		const {
			query,
			answerString,
			source,
			feedbackSentiment,
			feedbackOptions,
			additionalFeedbackAdded,
			includeResponse,
			canContact,
		} = props;
		return {
			action: 'submitted',
			actionSubject: 'aiAnswerFeedback',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				feedbackSentiment,
				feedbackOptions,
				additionalFeedbackAdded,
				includeResponse,
				canContact,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onReportButtonClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerReportButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onFollowUpSuggestionClick: ({ followUpString, destination, ...props }: commonAttributesType & {
	followUpString: string;
	destination?: string;
}) => AnalyticsEventPayload = ({
	followUpString,
	destination,
	...props
}: commonAttributesType & {
	followUpString: string;
	destination?: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiFollowUpSuggestion',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				followUpHash: sha256Hash(followUpString),
				destination,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				followUpString,
			},
		};
	};

export const onErrorStateSuggestedQueriesShown: ({ showErrorStateSuggestedQueries, ...props }: commonAttributesType & {
	showErrorStateSuggestedQueries: string[];
}) => AnalyticsEventPayload = ({
	showErrorStateSuggestedQueries,
	...props
}: commonAttributesType & {
	showErrorStateSuggestedQueries: string[];
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'shown',
			actionSubject: 'aiErrorStateSuggestedQueries',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				showErrorStateSuggestedQueries,
			},
		};
	};

export const onErrorStateSuggestedQueryClick: ({ errorStateSuggestedQuery, ...props }: commonAttributesType & {
	errorStateSuggestedQuery: string;
}) => AnalyticsEventPayload = ({
	errorStateSuggestedQuery,
	...props
}: commonAttributesType & {
	errorStateSuggestedQuery: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiErrorStateSuggestedQuery',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				errorStateSuggestedQueryHash: sha256Hash(errorStateSuggestedQuery),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				errorStateSuggestedQuery,
			},
		};
	};

export const onOpenChatButtonClick: ({ experimentCohort, ...props }: commonAttributesType & {
	experimentCohort?: string;
}) => AnalyticsEventPayload = ({
	experimentCohort,
	...props
}: commonAttributesType & {
	experimentCohort?: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerOpenChatButton',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				experimentCohort,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onExpandButtonClicked: ({ expandButtonSource, ...props }: commonAttributesType & {
	expandButtonSource: string;
}) => AnalyticsEventPayload = ({
	expandButtonSource,
	...props
}: commonAttributesType & {
	expandButtonSource: string;
}): AnalyticsEventPayload => {
		const { query } = props;
		return {
			action: 'clicked',
			actionSubject: 'expandButton',
			actionSubjectId: expandButtonSource,
			source: props.source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
			},
		};
	};

// Click on primary result user profile for who question-answer
export const onWhoPrimaryViewProfileClick: ({ primaryUserName, ...props }: commonAttributesType & {
	primaryUserName: string;
}) => AnalyticsEventPayload = ({
	primaryUserName,
	...props
}: commonAttributesType & {
	primaryUserName: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerWhoPrimaryViewProfile',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				primaryUserName,
			},
		};
	};

// Right click on primary result user profile for who question-answer
export const onWhoPrimaryViewProfileContextMenuClick: ({ primaryUserName, ...props }: commonAttributesType & {
	primaryUserName: string;
}) => AnalyticsEventPayload = ({
	primaryUserName,
	...props
}: commonAttributesType & {
	primaryUserName: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'contextMenuClicked',
			actionSubject: 'aiAnswerWhoPrimaryViewProfile',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				primaryUserName,
			},
		};
	};

// Click on primary result pill e.g. one of the frequent collaborators or locations for who question-answer
export const onWhoPrimaryCoordinationPillClick: ({ resultSection, resultTitle, ...props }: commonAttributesType & {
	resultSection: string;
	resultTitle: string;
}) => AnalyticsEventPayload = ({
	resultSection,
	resultTitle,
	...props
}: commonAttributesType & {
	resultSection: string;
	resultTitle: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerWhoPrimaryCoordinationPill',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				resultSection,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				resultTitle,
			},
		};
	};

// Right click on primary result pill e.g. one of the frequent collaborators or locations for who question-answer
export const onWhoPrimaryCoordinationPillContextMenuClick: ({ resultSection, resultTitle, ...props }: commonAttributesType & {
	resultSection: string;
	resultTitle: string;
}) => AnalyticsEventPayload = ({
	resultSection,
	resultTitle,
	...props
}: commonAttributesType & {
	resultSection: string;
	resultTitle: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'contextMenuClicked',
			actionSubject: 'aiAnswerWhoPrimaryCoordinationPill',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				resultSection,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				resultTitle,
			},
		};
	};

// Click on additional match user profile for who question-answer
export const onWhoAdditionalMatchProfileClick: ({ additionalMatchUserName, additionalMatchTitle, ...props }: commonAttributesType & {
	additionalMatchUserName: string;
	additionalMatchTitle: string | null;
}) => AnalyticsEventPayload = ({
	additionalMatchUserName,
	additionalMatchTitle,
	...props
}: commonAttributesType & {
	additionalMatchUserName: string;
	additionalMatchTitle: string | null;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerWhoAdditionalMatchViewProfile',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				additionalMatchUserName,
				additionalMatchTitle,
			},
		};
	};

// Right click on additional match user profile for who question-answer
export const onWhoAdditionalMatchProfileContextMenuClick: ({ additionalMatchUserName, additionalMatchTitle, ...props }: commonAttributesType & {
	additionalMatchUserName: string;
	additionalMatchTitle: string | null;
}) => AnalyticsEventPayload = ({
	additionalMatchUserName,
	additionalMatchTitle,
	...props
}: commonAttributesType & {
	additionalMatchUserName: string;
	additionalMatchTitle: string | null;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'contextMenuClicked',
			actionSubject: 'aiAnswerWhoAdditionalMatchViewProfile',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
				additionalMatchUserName,
				additionalMatchTitle,
			},
		};
	};

export const onRecentWorkItemClick: ({ index, url, ...props }: commonAttributesType & {
	index: number;
	url: string;
}) => AnalyticsEventPayload = ({
	index,
	url,
	...props
}: commonAttributesType & {
	index: number;
	url: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerRecentWorkItem',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				index,
			},
			nonPrivacySafeAttributes: {
				query,
				url,
				answerString,
			},
		};
	};

export const onRecentWorkItemContextMenuClick: ({ index, url, ...props }: commonAttributesType & {
	index: number;
	url: string;
}) => AnalyticsEventPayload = ({
	index,
	url,
	...props
}: commonAttributesType & {
	index: number;
	url: string;
}): AnalyticsEventPayload => {
		const { query, answerString, source } = props;
		return {
			action: 'contextMenuClicked',
			actionSubject: 'aiAnswerRecentWorkItem',
			source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				index,
			},
			nonPrivacySafeAttributes: {
				query,
				url,
				answerString,
			},
		};
	};

export const onRecentWorkLinkClick: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerRecentWorkListLink',
		source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onRecentWorkLinkContextMenuClick: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'contextMenuClicked',
		actionSubject: 'aiAnswerRecentWorkListLink',
		source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Curated Definitions
// Megan Evans from ds team requested subjects be prepended with 'ai' for data pipeline purposes

// Event when definition edit button is clicked
export const onCuratedDefinitionEditButtonClicked: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, extraAttributes, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiCuratedDefinitionEditButton',
		source,
		eventType: 'ui',
		attributes: {
			// contentId and readingAidsSessionId passed in from Confluence readingAids
			...extraAttributes,
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Event when edit definition modal next button is clicked
export const onCuratedDefinitionNextButtonClicked: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, extraAttributes, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiCuratedDefinitionNextButton',
		source,
		eventType: 'ui',
		attributes: {
			// contentId and readingAidsSessionId passed in from Confluence readingAids
			...extraAttributes,
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Event when edit definition modal update button is clicked
export const onCuratedDefinitionUpdateButtonClicked: ({ commonAttributes, newDefinition, restrictedScope, sourceSelected, aiDefinitionEdited, }: {
	commonAttributes: commonAttributesType;
	newDefinition: string;
	restrictedScope: string;
	sourceSelected: boolean;
	aiDefinitionEdited: boolean;
}) => AnalyticsEventPayload = ({
	commonAttributes,
	newDefinition,
	restrictedScope,
	sourceSelected,
	aiDefinitionEdited,
}: {
	commonAttributes: commonAttributesType;
	newDefinition: string;
	restrictedScope: string;
	sourceSelected: boolean;
	aiDefinitionEdited: boolean;
}): AnalyticsEventPayload => {
		const {
			query: keyPhrase,
			answerString: oldDefinition,
			extraAttributes,
			source,
		} = commonAttributes;
		return {
			action: 'clicked',
			actionSubject: 'aiCuratedDefinitionUpdateButton',
			source,
			eventType: 'ui',
			attributes: {
				// contentId and readingAidsSessionId passed in from Confluence readingAids
				...extraAttributes,
				restrictedScope,
				sourceSelected,
				newDefinitionLength: newDefinition.length,
				// whether the user is editing an AI answer or already edited definition
				aiDefinitionEdited,
			},
			nonPrivacySafeAttributes: {
				keyPhrase,
				oldDefinition,
				newDefinition,
			},
		};
	};

export const onCuratedDefinitionSeeEditHistoryButtonClicked: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, extraAttributes, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiCuratedDefinitionSeeEditHistoryButton',
		source,
		eventType: 'ui',
		attributes: {
			// contentId and readingAidsSessionId passed in from Confluence readingAids
			...extraAttributes,
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

export const onCuratedDefinitionEditButtonShown: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, answerString, extraAttributes, source } = props;
	return {
		action: 'shown',
		actionSubject: 'aiCuratedDefinitionEditButton',
		source,
		eventType: 'track',
		attributes: {
			...extraAttributes,
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// Tracking clicks on the copy button in SAIN answer cards
export const onAIAnswerCopyButtonClicked: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, answerString, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerCopy',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
			answerString,
		},
	};
};

// When the read more button is initially shown to the user
export const onReadMoreButtonShown: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerReadMoreButton',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the user clicks the read more button
export const onReadMoreButtonClicked: (props: commonAttributesType) => AnalyticsEventPayload = (props: commonAttributesType): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'clicked',
		actionSubject: 'aiAnswerReadMoreButton',
		source: source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// When the knowledge card loading dialog is initially shown to the user
export const onKnowledgeCardLoadingDialogShown: (props: commonAttributesType, isAnimated?: boolean) => AnalyticsEventPayload = (
	props: commonAttributesType,
	isAnimated?: boolean,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'shown',
		actionSubject: 'knowledgeCardLoadingDialog',
		source: source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
			isAnimated,
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

// Tracking when the jira nl to jql card was loaded. Includes both failures and success.
export const onJiraNlToJqlCardLoaded: ({ searchSessionId, queryHash, brand, source, query, loadTime, success, answerCardType, answerString, endState, }: NonGenerativeAIAnswerResultShownProps & {
	loadTime?: number;
	success: boolean;
	endState?: "answered" | "no-issues" | "jql-error" | "issues-error";
}) => AnalyticsEventPayload = ({
	searchSessionId,
	queryHash,
	brand,
	source,
	query,
	loadTime,
	success,
	answerCardType,
	answerString,
	endState,
}: NonGenerativeAIAnswerResultShownProps & {
	loadTime?: number;
	success: boolean;
	endState?: 'answered' | 'no-issues' | 'jql-error' | 'issues-error';
}): AnalyticsEventPayload => {
		return {
			action: 'loaded',
			actionSubject: 'aiAnswerJiraNlToJqlCard',
			source,
			eventType: 'track',
			attributes: {
				searchSessionId,
				queryHash,
				brand,
				source,
				loadTime,
				success,
				answerCardType,
				endState,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

// When the user clicks the Jira issue key in the Jira NL to JQL smart card
export const onJiraIssueKeyClicked: ({ searchSessionId, queryHash, brand, source, query, issuekey, answerCardType, answerString, }: NonGenerativeAIAnswerResultShownProps & {
	issuekey?: string;
}) => AnalyticsEventPayload = ({
	searchSessionId,
	queryHash,
	brand,
	source,
	query,
	issuekey,
	answerCardType,
	answerString,
}: NonGenerativeAIAnswerResultShownProps & {
	issuekey?: string;
}): AnalyticsEventPayload => {
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerJiraCardWorkItemLink',
			source,
			eventType: 'ui',
			attributes: {
				searchSessionId,
				queryHash,
				brand,
				source,
				answerCardType,
			},
			nonPrivacySafeAttributes: {
				answerString,
				query,
				issuekey,
			},
		};
	};

// When the user clicks the view all "x" work items button
export const onJiraViewAllButtonClicked: ({ searchSessionId, queryHash, brand, source, query, answerCardType, answerString, }: NonGenerativeAIAnswerResultShownProps & {
	totalIssuesCount: number;
}) => AnalyticsEventPayload = ({
	searchSessionId,
	queryHash,
	brand,
	source,
	query,
	answerCardType,
	answerString,
}: NonGenerativeAIAnswerResultShownProps & {
	totalIssuesCount: number;
}): AnalyticsEventPayload => {
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerJiraCardViewAll',
			source,
			eventType: 'ui',
			attributes: {
				searchSessionId,
				queryHash,
				brand,
				source,
				answerCardType,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onAIAnswerDialogClick: (props: commonAttributesType & {
	elementInteracted?: string;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		elementInteracted?: string;
	},
): AnalyticsEventPayload => {
		const { source, query, answerString, elementInteracted } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerDialog',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				elementInteracted,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export enum SmartLinksAnalyticsContextLocation {
	AI_DEFINITION = 'ai-definition',
	SAIN_AGENT_CARD = 'sain-agent-card',
	SAIN_BOOKMARK = 'sain-bookmark',
	SAIN_JIRA_ISSUE_CARD = 'sain-jira-issue-card',
	SAIN_JIRA_NL_TO_JQL_CARD = 'sain-jira-nl-to-jql-card',
	SAIN_PERSON_CARD = 'sain-person-card',
	SAIN_Q_AND_A_CARD = 'sain-q-and-a-card',
	SAIN_TEAM_CARD = 'sain-team-card',
	SEARCH_RESULT_CONFLUENCE_CARD = 'search-result-confluence-card',
}

export const getSmartLinksAnalyticsContextData: ({ location, commonAttributes, }: {
	location: SmartLinksAnalyticsContextLocation;
	commonAttributes: commonAttributesType;
}) => {
	attributes: {
		searchSessionId: string;
		queryHash: string;
		baseQueryHash: string;
		wordCount: number;
		queryLength: number;
		answerHash: string;
		appliedFilters: import("./types").parsedFiltersType | undefined;
		answerFormat: import("./types").NLPSearchResultFormat | null;
		answerLength: number;
		answerWordCount: number;
		answerCardType: import("../constants").AnswerCardType;
		followUpsCount: number;
		followUpsLength: number[];
		has3PSources: boolean;
		sources: {
			id: string;
			type: string;
		}[];
		sourceProducts: {
			[productName: string]: number;
		};
		apiSource: "assistance-service" | "cc-search-nlp" | "xp-knowledge-discovery";
		brand: import("..").AiBrand;
		sessionId: string;
		isHello: boolean;
		location: SmartLinksAnalyticsContextLocation;
	};
	location: SmartLinksAnalyticsContextLocation;
} = ({
	location,
	commonAttributes,
}: {
	location: SmartLinksAnalyticsContextLocation;
	commonAttributes: commonAttributesType;
}) => {
		return {
			attributes: {
				location,
				...filterCommonAttributes(commonAttributes),
			},
			// Below is added for the future implementation of Linking Platform namespaced analytic context
			location,
		};
	};

export type SmartLinksAnalyticsContextData = ReturnType<
	typeof getSmartLinksAnalyticsContextData
> & { attributes: Record<string, any> }; // Support arbitrary attributes, but require `location` attribute

// When user starts hovering over an AI-related element
export const onAIResultHoverStart: (props: commonAttributesType & {
	elementType: string;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		elementType: string;
	},
): AnalyticsEventPayload => {
		const { source, elementType } = props;
		return {
			action: 'viewed',
			actionSubject: 'aiResultHover',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				elementType,
			},
		};
	};

// When user stops hovering over an AI-related element
export const onAIResultHoverStop: (props: commonAttributesType & {
	elementType: string;
	hoverTime: number;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		elementType: string;
		hoverTime: number;
	},
): AnalyticsEventPayload => {
		const { source, elementType, hoverTime } = props;
		return {
			action: 'dismissed',
			actionSubject: 'aiResultHover',
			source: source,
			eventType: 'track',
			attributes: {
				...filterCommonAttributes(props),
				elementType,
				hoverTime,
			},
		};
	};

export const onAIAnswerTabClick: (props: commonAttributesType & {
	selectedTab: AIAnswerTab;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		selectedTab: AIAnswerTab;
	},
): AnalyticsEventPayload => {
		const { query, answerString, source, selectedTab } = props;
		return {
			action: 'clicked',
			actionSubject: 'aiAnswerTab',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				selectedTab,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onAIAnswerResultCompleteShown: (props: commonAttributesType & {
	answerHeight: number;
	containerHeight: number;
	whitespaceHeight: number;
	queryIntent?: QueryIntent;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		answerHeight: number;
		containerHeight: number;
		whitespaceHeight: number;
		queryIntent?: QueryIntent;
	},
): AnalyticsEventPayload => {
		const {
			query,
			answerString,
			source,
			answerHeight,
			containerHeight,
			whitespaceHeight,
			queryIntent,
		} = props;
		return {
			action: 'shown',
			actionSubject: 'aiAnswerResultComplete',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				answerHeight,
				containerHeight,
				whitespaceHeight,
				queryIntent,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onAIAnswerResultFirstByteShown: (props: commonAttributesType & {
	timeToFirstByte: number;
}) => AnalyticsEventPayload = (
	props: commonAttributesType & {
		timeToFirstByte: number;
	},
): AnalyticsEventPayload => {
		const { query, answerString, source, timeToFirstByte } = props;
		return {
			action: 'shown',
			actionSubject: 'aiAnswerResultFirstByte',
			source: source,
			eventType: 'ui',
			attributes: {
				...filterCommonAttributes(props),
				timeToFirstByte,
			},
			nonPrivacySafeAttributes: {
				query,
				answerString,
			},
		};
	};

export const onStreamingEarlyStopCardShown: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'shown',
		actionSubject: 'aiAnswerStreamingEarlyStopCard',
		source,
		eventType: 'track',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};

export const onStreamingEarlyStopCardDismissed: (props: commonAttributesType) => AnalyticsEventPayload = (
	props: commonAttributesType,
): AnalyticsEventPayload => {
	const { query, source } = props;
	return {
		action: 'dismissed',
		actionSubject: 'aiAnswerStreamingEarlyStopCard',
		source,
		eventType: 'ui',
		attributes: {
			...filterCommonAttributes(props),
		},
		nonPrivacySafeAttributes: {
			query,
		},
	};
};
